import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStableQuery } from '../../../shared/hooks/useStableQuery';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { useContext, useMemo } from 'react';
import { BaseContext } from '../../../shared/base-context';
import { useLoadingWrapper } from '../../../shared/hooks/useLoadingWrapper';
import { useState, useEffect } from 'react';
import { MainRoutes } from '../../../shared/models';
import { Button } from '../../../catalyst-components/button';
import { Input } from '../../../catalyst-components/input';
import { useMutation } from 'convex/react';
import { TrophyIcon, CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { ChatAboutButton } from './ChatAboutButton';

// Component to display a completed session card with fetched assistant info
const CompletedSessionCard = ({
  sessionId,
  assistantId
}: {
  sessionId: Id<"sessions">;
  assistantId: Id<"assistants">;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assistant = useStableQuery(
    api.assistants.getById,
    assistantId ? { id: assistantId } : "skip"
  );

  return (
    <div className="border border-border bg-white rounded-xl hover:shadow-md transition-all">
      <div className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 mt-1">
            <CheckCircleIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-foreground">
                {assistant?.display_name || assistant?.name || 'Session'}
              </h3>
              {assistant?.xp_reward && (
                <span className="text-xs font-medium text-primary">+{assistant.xp_reward} pts</span>
              )}
            </div>
            {assistant?.short_description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                {assistant.short_description}
              </p>
            )}
            {assistant?.assistant_type && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs border border-primary/30 text-primary bg-primary/5 px-2 py-1 rounded-md">
                  {assistant.assistant_type}
                </span>
              </div>
            )}
          </div>
        </div>
        <Button
          color="secondary"
          onClick={() => {
            navigate(`${MainRoutes.Chat}?sessionId=${sessionId}`);
          }}
          className="w-full"
        >
          {t('skill.reviewSession')}
        </Button>
      </div>
    </div>
  );
};

const isNewSession = (assignedAt?: string) => {
  if (!assignedAt) return false;
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(assignedAt).getTime() < fourteenDays;
};

export const TraitDetails = () => {
  const { t } = useTranslation();
  const { traitId } = useParams<{ traitId: string }>();
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);
  const [isLoading, setIsLoading] = useState(true);
  const LoadingWrapper = useLoadingWrapper({ isLoading });

  const skill = useStableQuery(
    api.skills.getById,
    traitId ? { id: traitId as Id<"skills"> } : "skip"
  );

  const completedSessions = useStableQuery(
    api.completed_sessions.listBySkillAndUser,
    convexUserId && traitId
      ? { skill_id: traitId as Id<"skills">, user_id: convexUserId as Id<"users"> }
      : "skip"
  );

  const sessionInProgress = useStableQuery(
    api.user_progress.getCurrentSessionInProgress,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  const incompleteAssistants = useStableQuery(
    api.skill_assistants.getIncompleteAssistantsForSkill,
    convexUserId && traitId
      ? { skill_id: traitId as Id<"skills">, user_id: convexUserId as Id<"users"> }
      : "skip"
  );

  const skillSummary = useStableQuery(
    api.user_skill_summary.getByUserAndSkill,
    convexUserId && traitId
      ? { user_id: convexUserId as Id<"users">, skill_id: traitId as Id<"skills"> }
      : "skip"
  );

  const userXpData = useStableQuery(
    api.user_xp.getUserXpWithLevelInfo,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  // Get skill XP data for this specific skill
  const skillXpData = useMemo(() => {
    if (!userXpData?.skill_xp || !traitId) return null;
    return userXpData.skill_xp.find(s => s.skill_id === traitId);
  }, [userXpData, traitId]);

  const currentXp = skillXpData?.xp || 0;
  const xpToNextLevel = skillXpData?.xp_to_next_level || 0;
  const nextLevelXp = currentXp + xpToNextLevel;
  const currentLevel = skillXpData?.level || 1;
  const progressPercent = nextLevelXp > 0 ? Math.round((currentXp / nextLevelXp) * 100) : 0;
  const isMaxLevel = skillXpData?.is_max_level || false;

  const startSession = useMutation(api.sessions.startSession);
  const [startingSessionId, setStartingSessionId] = useState<Id<"assistants"> | null>(null);
  const [filterText, setFilterText] = useState('');

  // Filter assistants based on search text
  const filteredAssistants = useMemo(() => {
    if (!incompleteAssistants || !filterText.trim()) {
      return incompleteAssistants || [];
    }

    const searchLower = filterText.toLowerCase().trim();
    return incompleteAssistants.filter(assistant => {
      const name = (assistant.assistantDisplayName || assistant.name || '').toLowerCase();
      const purpose = (assistant.assistantPurpose || '').toLowerCase();
      const description = (assistant.description || '').toLowerCase();

      return name.includes(searchLower) ||
             purpose.includes(searchLower) ||
             description.includes(searchLower);
    });
  }, [incompleteAssistants, filterText]);

  useEffect(() => {
    if (skill !== undefined) {
      setIsLoading(false);
    }
  }, [skill]);

  if (!traitId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground text-lg">{t('skill.notFound')}</p>
        <Button color="primary" className="mt-4" onClick={() => navigate(MainRoutes.Skills)}>
          {t('skill.backToSkills')}
        </Button>
      </div>
    );
  }

  return (
    <LoadingWrapper>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="px-6 py-8 border-b border-border">
          <div className="max-w-4xl mx-auto pt-20">
            <Button
              color="light"
              onClick={() => navigate(MainRoutes.Skills)}
              className="mb-4"
            >
              {t('common.backHome')}
            </Button>

            {skill && (
              <>
                <h1 className="text-3xl font-semibold mb-4 text-foreground">{skill.name}</h1>

                {/* Level Badge & Points Display */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="px-4 py-2 rounded-full bg-primary/10 border-2 border-primary/30">
                    <div className="flex items-center gap-2">
                      <TrophyIcon className="h-5 w-5 text-primary" />
                      <span className="text-lg font-semibold text-primary">{t('skill.levelBadge', { level: currentLevel })}</span>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    {!isMaxLevel && (
                      <span className="text-2xl font-semibold text-foreground">{t('skill.pointsDisplay', { currentXp, nextLevelXp })}</span>
                    )}
                    {isMaxLevel && (
                      <>
                        <span className="text-2xl font-semibold text-foreground">{currentXp}</span>
                        <span className="text-sm text-muted-foreground">{t('skill.maxLevelMessage')}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                {!isMaxLevel && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('skill.progressToNextLevel', { xpToNextLevel, nextLevel: currentLevel + 1 })}
                  </p>
                )}
                {isMaxLevel && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {t('skill.maxLevelReached')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-8">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* About This Skill */}
            {skill?.description && (
              <div>
                <h2 className="font-semibold text-foreground mb-3">{t('skill.aboutTitle')}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {skill.description}
                </p>
              </div>
            )}

            {/* Skill Summary */}
            {skillSummary?.summary && (
              <div className="p-6 bg-gradient-to-br from-secondary/30 to-white rounded-2xl border border-border">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h2 className="font-semibold text-foreground">{t('skill.progressSummaryTitle')}</h2>
                  {completedSessions && completedSessions.length > 0 && (
                    <ChatAboutButton
                      contextType="skill_summary"
                      contextId={`skill_summary_${traitId}`}
                      systemMessage={`User tapped Discuss on their "${skill?.name}" skill summary. Summary: "${skillSummary.summary}". Level ${currentLevel}, ${currentXp} XP. Call get_skill_progress for this skill, then respond in 1-2 sentences. Keep it brief.`}
                      autoSubmitPrompt={`I'd like to talk about my progress in ${skill?.name}.`}
                      metadata={{ skill_id: traitId as Id<"skills"> }}
                      label="Discuss"
                      variant="text"
                    />
                  )}
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {skillSummary.summary}
                </p>
              </div>
            )}

            {/* Sessions for You (Incomplete) */}
            {incompleteAssistants && incompleteAssistants.length > 0 && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h2 className="font-semibold text-foreground">{t('skill.sessionsTitle')}</h2>
                  <div className="w-full sm:w-64">
                    <Input
                      type="search"
                      placeholder={t('skill.filterPlaceholder')}
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className={`space-y-3${sessionInProgress?.session_id ? " opacity-50" : ""}`}>
                  {filteredAssistants.length > 0 ? (
                    filteredAssistants.map((assistant) => {
                      const isStarting = startingSessionId === assistant.assistant_id;
                      return (
                        <div
                          key={assistant._id}
                          className="border border-border hover:shadow-md transition-all bg-white rounded-xl"
                        >
                          <div className="p-6">
                            <div className="flex items-start gap-4 mb-4">
                              <div className="flex-shrink-0 mt-1">
                                <div className="h-5 w-5 rounded-full border-2 border-accent bg-accent/20" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <h3 className="font-semibold text-foreground">
                                    {assistant.assistantDisplayName || assistant.name}
                                  </h3>
                                </div>
                                {assistant.assistantDescription && (
                                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                                    {assistant.assistantDescription}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                  <span className="text-xs border border-primary/30 text-primary bg-primary/5 px-2 py-1 rounded-md">
                                    {assistant.assistantType}
                                  </span>
                                  {assistant.multipleChoice && (
                                    <span className="text-xs border border-primary/30 text-primary bg-primary/5 px-2 py-1 rounded-md">
                                      Quiz
                                    </span>
                                  )}
                                  {isNewSession(assistant.assigned_at) && (
                                    <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-md font-bold shadow-sm">
                                      {t('common.new')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              color="primary"
                              onClick={async () => {
                                if (!convexUserId || isStarting) return;
                                try {
                                  setStartingSessionId(assistant.assistant_id);
                                  const result = await startSession({
                                    user_id: convexUserId as Id<"users">,
                                    assistant_id: assistant.assistant_id,
                                  });
                                  if (result?.session_id) {
                                    navigate(`${MainRoutes.Chat}?sessionId=${result.session_id}`);
                                  }
                                } catch (error) {
                                  setStartingSessionId(null);
                                }
                              }}
                              disabled={!!sessionInProgress || isStarting}
                              className="px-6 py-2"
                            >
                              {isStarting ? (
                                <span className="flex items-center justify-center gap-2">
                                  <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-4 w-4" />
                                  {t('browse.submitLoading')}
                                </span>
                              ) : (
                                t('browse.submitButton')
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>{t('skill.noSessionsMessage')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completed Sessions */}
            {completedSessions && completedSessions.length > 0 && (
              <div>
                <h2 className="font-semibold text-foreground mb-4">{t('skill.completedSessionsTitle')}</h2>
                <div className="space-y-3">
                  {completedSessions.map((session) => (
                    <CompletedSessionCard
                      key={session._id}
                      sessionId={session.session_id}
                      assistantId={session.assistant_id}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bottom Spacing for mobile safe area */}
            <div className="h-40 pb-safe"></div>
          </div>
        </div>

        {!skill && !isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <p className="text-muted-foreground text-lg mb-4">{t('skill.notFound')}</p>
            <Button color="primary" onClick={() => navigate(MainRoutes.Skills)}>
              {t('common.backHome')}
            </Button>
          </div>
        )}
      </div>
    </LoadingWrapper>
  );
};
